import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Flag, AlertTriangle, Info } from 'lucide-react';
import ProgrammeDialog from './ProgrammeDialog';
import { closePatientProgramme } from '../../redux/programmeSlice';
import { LIMITS, collapse, defaultLifeEventTitle, todayIso, dayNumber } from '../../utils/careProgrammes';
import { serverReason } from '../patientTabs/prescriptionHelpers';

// CLOSE PROGRAMME — the outcome (free text, required) and, for a DOCTOR, the
// option to add a life event to the patient's health record (title defaults
// to "<programme> — <outcome>"). The admin may close but not write the life
// event: an administrator never writes clinical history in a doctor's name
// (contract §3, 403 LIFE_EVENT_DOCTOR_ONLY) — the option is not offered.
// The closed programme stays on the record, its timeline frozen at today.
function CloseProgrammeModal({ isOpen, patientId, programme, appointmentId = null, onClose }) {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const role = (typeof localStorage !== 'undefined' && localStorage.getItem('Role')) || '';
    const mayAddLifeEvent = role === 'doctor';
    const busy = useSelector((state) => Boolean(programme && state.programmes?.acting === programme.patientProgrammeId));
    const [outcome, setOutcome] = useState('');
    const [addLifeEvent, setAddLifeEvent] = useState(false);
    const [title, setTitle] = useState('');
    const [occurredAt, setOccurredAt] = useState('');
    const [showErrors, setShowErrors] = useState(false);
    const [serverError, setServerError] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        setOutcome('');
        setAddLifeEvent(false);
        setTitle('');
        setOccurredAt(todayIso());
        setShowErrors(false);
        setServerError('');
    }, [isOpen, programme?.patientProgrammeId]);

    if (!programme) return null;

    const outcomeErr = !collapse(outcome) ? 'err_outcome_required' : '';
    const dateErr = addLifeEvent && !Number.isFinite(dayNumber(occurredAt)) ? 'err_life_event_date' : '';

    const submit = async () => {
        setShowErrors(true);
        if (outcomeErr || dateErr || busy) return;
        setServerError('');
        const res = await dispatch(closePatientProgramme({
            patientId,
            patientProgrammeId: programme.patientProgrammeId,
            outcome: collapse(outcome),
            addLifeEvent: mayAddLifeEvent && addLifeEvent,
            title: collapse(title) || null,
            occurredAt,
            appointmentId,
        }));
        if (closePatientProgramme.fulfilled.match(res)) {
            toast.success(res.payload?.lifeEventEntryId ? t('programmes.closed_with_event') : t('programmes.closed'));
            onClose?.();
            return;
        }
        setServerError(serverReason(res.payload, t('programmes.close_failed'), i18n.language));
    };

    return (
        <ProgrammeDialog
            isOpen={isOpen}
            testId="close-programme"
            title={t('programmes.close_title', { name: programme.name })}
            icon={Flag}
            onClose={onClose}
            onSubmit={submit}
            submitLabel={t('programmes.close_confirm')}
            busy={busy}
            danger
        >
            <p className="text-sm text-gray-600">{t('programmes.close_text')}</p>
            <div>
                <label htmlFor="close-programme-outcome" className="mb-1 block text-xs font-semibold text-gray-600">{t('programmes.outcome_label')}</label>
                <textarea
                    id="close-programme-outcome"
                    data-testid="close-programme-outcome"
                    rows={3}
                    maxLength={LIMITS.outcome}
                    value={outcome}
                    placeholder={t('programmes.outcome_placeholder')}
                    onChange={(e) => { setOutcome(e.target.value); setServerError(''); }}
                    className={`w-full rounded-xl border px-3 py-2 text-sm focus:border-[#4ABDBD] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/40 ${showErrors && outcomeErr ? 'border-red-300' : 'border-gray-200'}`}
                />
                {showErrors && outcomeErr && <p data-testid="close-programme-outcome-error" className="mt-1 text-xs text-red-600">{t(`programmes.${outcomeErr}`)}</p>}
            </div>

            {mayAddLifeEvent ? (
                <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-3">
                    <label className="flex cursor-pointer items-start gap-2 text-sm font-semibold text-gray-700">
                        <input
                            type="checkbox"
                            data-testid="close-programme-life-event"
                            checked={addLifeEvent}
                            onChange={(e) => setAddLifeEvent(e.target.checked)}
                            className="mt-0.5 h-4 w-4 accent-[#4ABDBD]"
                        />
                        <span>
                            {t('programmes.add_life_event')}
                            <span className="block text-[11px] font-normal text-gray-500">{t('programmes.add_life_event_hint')}</span>
                        </span>
                    </label>
                    {addLifeEvent && (
                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
                            <div>
                                <label htmlFor="close-programme-title" className="mb-1 block text-xs font-semibold text-gray-600">{t('programmes.life_event_title')}</label>
                                <input
                                    id="close-programme-title"
                                    data-testid="close-programme-title"
                                    type="text"
                                    maxLength={LIMITS.lifeEventTitle}
                                    value={title}
                                    placeholder={defaultLifeEventTitle(programme.name, outcome || t('programmes.outcome_label'))}
                                    onChange={(e) => setTitle(e.target.value)}
                                    className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:border-[#4ABDBD] focus:outline-none"
                                />
                            </div>
                            <div>
                                <label htmlFor="close-programme-date" className="mb-1 block text-xs font-semibold text-gray-600">{t('programmes.life_event_date')}</label>
                                <input
                                    id="close-programme-date"
                                    data-testid="close-programme-date"
                                    type="date"
                                    value={occurredAt}
                                    onChange={(e) => setOccurredAt(e.target.value)}
                                    className={`rounded-xl border bg-white px-3 py-2 text-sm focus:border-[#4ABDBD] focus:outline-none ${showErrors && dateErr ? 'border-red-300' : 'border-gray-200'}`}
                                />
                            </div>
                            {showErrors && dateErr && <p className="text-xs text-red-600 sm:col-span-2">{t(`programmes.${dateErr}`)}</p>}
                        </div>
                    )}
                </div>
            ) : (
                <p data-testid="close-programme-no-life-event" className="flex items-start gap-1.5 text-[11px] text-gray-500">
                    <Info className="mt-px h-3.5 w-3.5 shrink-0 text-[#2D8A8A]" aria-hidden="true" />
                    {t('programmes.life_event_doctor_only')}
                </p>
            )}

            {serverError && (
                <p role="alert" data-testid="close-programme-error" className="flex items-center gap-2 rounded-xl border border-red-100 bg-red-50 p-3 text-xs font-medium text-red-700">
                    <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {serverError}
                </p>
            )}
        </ProgrammeDialog>
    );
}

export default CloseProgrammeModal;
